import { call, put, select, takeEvery } from "redux-saga/effects";

import {
    FORM_CLOSED,
    FORM_CUSTOM_TITLE_CHANGED,
    FORM_PROFILE_CHANGED,
    IFormClosedAction,
    IFormCustomTitleChangedAction,
    IFormOpenedAction,
    IFormProfileChangedAction,
} from "@multiweb/reactCore/bridge";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { ProfileChangeType } from "@multiweb/uiShared/ProfileChangeType";
import { logger } from "../logger";
import { getFormItem, getFormProfile } from "../selectors";
import { formOpenedAndShowInMenuActionPattern } from "../utils";
import {
    createRemoveGroupMapItemAction,
    createRemoveLabelInfoAction,
    createRemoveNumberingGroupAction,
    createRemoveOpenedItemNumberAction,
    createSetGroupMapItemAction,
    createSetLabelInfoAction,
    createSetNumberingGroupAction,
    createSetOpenedItemNumberAction,
} from "./actions";
import { NO_MENU_ITEM_LABEL } from "./constants";
import {
    getCustomTitle,
    getFormInstanceLabelInfo,
    getGroup,
    getGroupKey,
    getMaxInstanceNumberForGroup,
    getNumberOfItemsInGroup,
    getOpenedFormInfo,
    getOpenedFormsInfo,
} from "./selectors";
import { LabelInfo, NumberingGroupInfo } from "./types";

export const saga: SagaWithReturnType = function* openedItemsSaga() {
    yield takeEvery(formOpenedAndShowInMenuActionPattern, formOpenedSaga);
    yield takeEvery(FORM_CLOSED, formClosedSaga);
    yield takeEvery(FORM_CUSTOM_TITLE_CHANGED, customTitleChangedSaga);
    yield takeEvery(FORM_PROFILE_CHANGED, formProfileChangedSaga);
};

/**
 * Obsluhuje událost otevření formuláře, jehož otevřená položka má být viditelná v menu (má nastaveno showInMenu=true). Pro nově otevřený formulář vytvoří informace o popisku a číslování.
 * @param action
 */
export const formOpenedSaga: SagaWithReturnType<[IFormOpenedAction]> = function* formOpenedSaga(action) {
    const { formInstanceId } = action.payload.openedFormInfo;
    yield call(createAndSetLabelInfoSaga, formInstanceId);
    yield call(addOpenedItemToNumberingGroupSaga, formInstanceId);
};

/**
 * Obsluhuje událost zavření formuláře. Vyčistí informace o popisku a číslování pro zavřený formulář.
 * @param action
 */
export const formClosedSaga: SagaWithReturnType<[IFormClosedAction]> = function* formClosedSaga(action) {
    const { formInstanceId } = action.payload.closedFormInfo;
    yield put(createRemoveLabelInfoAction(formInstanceId));
    yield call(removeOpenedItemFromNumberingGroupSaga, formInstanceId);
};

/**
 * Obsluhuje událost změny custom title. Aktualizuje popisek a číslování pro odpovídající otevřenou položku.
 * @param action
 */
export const customTitleChangedSaga: SagaWithReturnType<[IFormCustomTitleChangedAction]> = function* customTitleChangedSaga(action) {
    const { formInstanceId } = action.payload;
    yield call(createAndSetLabelInfoSaga, formInstanceId);
    yield call(changeNumberingGroupOfOpenedItemSaga, formInstanceId);
};

/**
 * Obsluhuje událost změny profilu. Aktualizuje popisek a číslování pro všechny otevřené položky upraveného profilu.
 * Při odebrání profilu zůstává jeho otevřená položka v původním stavu.
 * @param action
 */
export const formProfileChangedSaga: SagaWithReturnType<[IFormProfileChangedAction]> = function* (action) {
    const { type, profile } = action.payload;
    if (type === ProfileChangeType.update) {
        const openedFormsInfo: ReturnType<typeof getOpenedFormsInfo> = yield select(getOpenedFormsInfo);
        const affectedOpenedProfiles = openedFormsInfo
            .filter(({ profileId }) => profileId === profile.id)
            .reverse(); // Musí být otočené, protože pořadí vykreslování je opačné než pořadí otevření. Takto se zachovají čísla při změně skupiny.
        for (const { formInstanceId } of affectedOpenedProfiles) {
            yield call(createAndSetLabelInfoSaga, formInstanceId);
            yield call(changeNumberingGroupOfOpenedItemSaga, formInstanceId);
        }
    }
};

/**
 * Vytvoří klíč skupiny pro číslování pro zadanou otevřenou položku. Prerekvizitou je, že otevřená položka již má ve stavu informace o svém popisku.
 * V případě, že nelze získat potřebná data pro vytvoření klíče vrací undefined a vypisuje chybovou hlášku.
 * @param formInstanceId
 */
export const createGroupKeySaga: SagaWithReturnType<[string], string | undefined> = function* createGroupKeySaga(formInstanceId) {
    const openedFormInfo: ReturnType<typeof getOpenedFormInfo> = yield select(getOpenedFormInfo, formInstanceId);
    if (!openedFormInfo) {
        yield call(logger.error, "Cannot get opened form info for opened item with id %s. Opened item numbering may be in inconsistent state.", formInstanceId);
        return undefined;
    }
    const { formCode } = openedFormInfo;
    const labelInfo: ReturnType<typeof getFormInstanceLabelInfo> = yield select(getFormInstanceLabelInfo, formInstanceId);
    if (!labelInfo) {
        yield call(logger.error, "Cannot get label info for opened item with id %s. Opened item numbering may be in inconsistent state.", formInstanceId);
        return undefined;
    }
    return `${formCode}-${labelInfo.labelValue}`;
};

/**
 * Přesune zadanou otevřenou položku do jiné skupiny číslování. Prerekvizitou je, že otevřená položka má nastavené ve stavu aktuální informace o popisku.
 * @param formInstanceId
 */
export const changeNumberingGroupOfOpenedItemSaga: SagaWithReturnType<[string]> = function* changeNumberingGroupOfOpenedItemSaga(formInstanceId) {
    yield call(removeOpenedItemFromNumberingGroupSaga, formInstanceId);
    yield call(addOpenedItemToNumberingGroupSaga, formInstanceId);
};

/**
 * Přidá zadanou otevřenou položku do skupiny číslování. Prerekvizitou je, že otevřená položka má nastavené ve stavu aktuální informace o popisku.
 * @param formInstanceId
 */
export const addOpenedItemToNumberingGroupSaga: SagaWithReturnType<[string]> = function* addOpenedItemToGroupSaga(formInstanceId) {
    const groupKey: SagaYieldReturn<typeof createGroupKeySaga> = yield call(createGroupKeySaga, formInstanceId);
    if (!groupKey) {
        return;
    }
    const group: NumberingGroupInfo | undefined = yield select(getGroup, groupKey);
    if (group) {
        const { counter } = group;
        const newNumber = counter + 1;
        yield put(createSetNumberingGroupAction(groupKey, { counter: newNumber, hadMoreMembers: true }));
        yield put(createSetOpenedItemNumberAction(formInstanceId, newNumber));
    } else {
        const initialNumber = 1;
        yield put(createSetNumberingGroupAction(groupKey, { counter: initialNumber, hadMoreMembers: false }));
        yield put(createSetOpenedItemNumberAction(formInstanceId, initialNumber));
    }
    yield put(createSetGroupMapItemAction(formInstanceId, groupKey));
};

/**
 * Odebere zadanou otevřenou položku ze skupiny číslování. Prerekvizitou je, že otevřená položka má nastavené ve stavu aktuální informace o popisku.
 * @param formInstanceId
 */
export const removeOpenedItemFromNumberingGroupSaga: SagaWithReturnType<[string]> = function* removeOpenedItemFromNumberingGroupSaga(formInstanceId) {
    const groupKey: ReturnType<typeof getGroupKey> = yield select(getGroupKey, formInstanceId);
    if (!groupKey) {
        yield call(logger.error, "Cannot get numbering group key for opened item %s. Opened item numbering may be in inconsistent state.", formInstanceId);
        return;
    }
    yield put(createRemoveOpenedItemNumberAction(formInstanceId));
    yield put(createRemoveGroupMapItemAction(formInstanceId));

    const numberOfItemsInGroup: ReturnType<typeof getNumberOfItemsInGroup> = yield select(getNumberOfItemsInGroup, groupKey);
    if (numberOfItemsInGroup > 0) { // skupina má jiné položky, aktualizace skupiny
        const maxInstanceNumber: ReturnType<typeof getMaxInstanceNumberForGroup> = yield select(getMaxInstanceNumberForGroup, groupKey);
        yield put(createSetNumberingGroupAction(groupKey, { counter: maxInstanceNumber, hadMoreMembers: true }));
    } else { // skupina již nemá žádnou položku, zrušení celé skupiny
        yield put(createRemoveNumberingGroupAction(groupKey));
    }
};

/**
 * Zajistí vytvoření informací o popisku a jejich vložení do stavu.
 * @param formInstanceId Id otevřené položky, pro které se vytváří popisek.
 */
export const createAndSetLabelInfoSaga: SagaWithReturnType<[string]> = function* createAndSetLabelInfoSaga(formInstanceId) {
    const labelInfo: SagaYieldReturn<typeof getLabelValueSaga> = yield call(getLabelValueSaga, formInstanceId);
    if (labelInfo) {
        const { labelValue, isLocKey } = labelInfo;
        yield put(createSetLabelInfoAction(formInstanceId, labelValue, isLocKey));
    }
};

/**
 * Získá informace o popisku pro otevřenou položku. Definuje priority zdrojů pro získání popisku.
 * @param formInstanceId
 */
export const getLabelValueSaga: SagaWithReturnType<[string], LabelInfo | undefined> = function* getLabelForInstanceSaga(formInstanceId) {
    const openedFormInfo: ReturnType<typeof getOpenedFormInfo> = yield select(getOpenedFormInfo, formInstanceId);
    if (!openedFormInfo) {
        yield call(logger.error, "Opened form info was not found for instance id %s. Cannot create LabelValue for opened item.", formInstanceId);
        return undefined;
    }
    const { profileId, menuItemId, titleLocKey } = openedFormInfo;

    let isLocKey;
    let labelValue;

    const customTitle: ReturnType<typeof getCustomTitle> = yield select(getCustomTitle, formInstanceId);
    if (customTitle) {
        labelValue = customTitle;
        isLocKey = false;
    } else if (profileId) {
        const profile: ReturnType<typeof getFormProfile> = yield select(getFormProfile, profileId);
        labelValue = profile?.title;
        isLocKey = false;
    } else if (menuItemId) {
        const formItem: ReturnType<typeof getFormItem> = yield select(getFormItem, menuItemId);
        labelValue = formItem?.locKey;
        isLocKey = true;
    } else if (titleLocKey) {
        labelValue = titleLocKey;
        isLocKey = true;
    } else {
        labelValue = NO_MENU_ITEM_LABEL;
        isLocKey = false;
    }

    return { labelValue, isLocKey };
};
